import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useFlowium } from '../client/FlowiumContext';

import AuthLayout from '../layout/AuthLayout';
import Button from '../components/Button';
import Input from '../components/Input';
import { EnvelopeIcon } from '../components/Icons';

const PasswordRecovery: React.FC = () => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { client } = useFlowium();

    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handlePasswordRecovery = async () => {
        if (!email) {
            setError('Email is required');
            return;
        }
        if (!isValidEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }
        setLoading(true);
        try {
            await client.requestPasswordReset(email);
            setMessage('Password recovery email sent successfully. Please check your inbox.');
            setError(''); // clear any previous error messages
        } catch (error) {
            setError('Error sending password recovery email');
            setMessage(''); // clear any previous success messages
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && email) {
            handlePasswordRecovery();
        }
    };

    return (
        <AuthLayout title="Forgot Your Password?">
            <p className="text-sm text-content-secondary mb-6">No worries, we'll send you reset instructions.</p>
            <Input
                label="Email"
                placeholder="einstein@princeton.edu"
                icon={<EnvelopeIcon />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading || Boolean(message)}
                className="mb-4"
            />

            <Button
                onClick={handlePasswordRecovery}
                disabled={loading || !email || Boolean(message)}
                className={`w-full`}
            >
                {loading ? 'Sending...' : 'Send Recovery Email'}
            </Button>

            {error && <p className="text-error mt-4">{error}</p>}
            {message && <p className="text-success mt-4">{message}</p>}

            {message ? (
                <p className="mt-4 text-center">
                    <Link to="/login" className="text-content hover:underline">Go to Login</Link>
                </p>
            ) : (
                <p className="mt-4 text-center text-sm font-light">
                    <span className="text-content-secondary">Remembered it?</span>
                    <button onClick={() => navigate('/login')} className="ml-1 text-accent hover:underline">
                        Login
                    </button>
                </p>
            )}
        </AuthLayout>
    );
};

export default PasswordRecovery;
